import React from "react";

import AccumulationChart from "../stocks/accumulation_chart";

const krw = (n) => "₩" + Math.round(Number(n) || 0).toLocaleString();
const krwSigned = (n) => {
    const v = Number(n) || 0;
    return (v >= 0 ? "+₩" : "-₩") + Math.abs(Math.round(v)).toLocaleString();
};
const pct = (n) => (n == null ? "—" : (n >= 0 ? "+" : "") + n.toFixed(2) + "%");

/** StockSummaryHero의 StatCell과 같은 셀 — 두 화면이 같은 밀도로 보이게 한다. */
function StatCell({ label, value, valueClass, wide }) {
    return (
        <div
            className={`rounded-xl border border-surface-border bg-surface-card-alt px-3 py-2.5 ${
                wide ? "col-span-2 sm:col-span-1" : ""
            }`}
        >
            <div className="text-xs text-content-muted">{label}</div>
            <div className={`mt-1 font-num text-lg font-semibold ${valueClass || "text-content"}`}>{value}</div>
        </div>
    );
}

/**
 * BtcDcaPanel — BTC 적립(DCA) 상세 (DAS-175 → DAS-177에서 해외주식 구조로 정렬).
 *
 * 해외주식 적립 화면과 같은 순서로 읽힌다: Hero 요약 → 적립 성과 차트 → 평단 대비 현재가.
 * 차트는 `components/stocks/accumulation_chart`를 **그대로 재사용**한다 — 문구만 주입한다.
 * 두 화면이 "비슷하게 생긴" 게 아니라 실제로 같은 컴포넌트여야 계속 같이 움직인다.
 *
 * 해외주식에 있으나 여기 없는 것과 그 이유:
 * - 계좌별 보기·포트폴리오 구성·보유 종목 표: 한 계좌·단일 자산이라 항상 1행/100%다.
 * - 환율 분해: 원화로만 사고파는 자산이라 분해할 환차익이 없다.
 * - 매수 타임라인: 체결 이력이 아직 없다 (업비트 주문 조회 연동 또는 수기 입력이 선행돼야 한다).
 */
class BtcDcaPanel extends React.Component {
    render() {
        const { summary, series, onGoDelta } = this.props;

        if (!summary || !summary.hasHolding) {
            return (
                <div className="rounded-2xl border border-surface-border bg-surface-card p-6 shadow-card">
                    <div className="text-sm font-semibold text-content-secondary">BTC 적립</div>
                    <p className="mt-2 text-xs leading-relaxed text-content-muted">
                        업비트 계좌에 보유 중인 BTC 현물이 없습니다. 현물을 매수하면 이곳에 적립 지표가 표시됩니다.
                    </p>
                </div>
            );
        }

        const { qty, value, principal, pnl, pnlPct, avgBuyPrice, currentPrice } = summary;
        const isGain = pnl >= 0;
        const pnlColor = isGain ? "text-gain" : "text-loss";
        // 평단 대비 현재가 막대 — 손익 방향에 따라 채움 비율을 정한다(최대 100%).
        const ratio =
            Number.isFinite(avgBuyPrice) && avgBuyPrice > 0 && Number.isFinite(currentPrice)
                ? currentPrice / avgBuyPrice
                : null;
        const fillPercent =
            ratio == null ? 50 : Math.max(4, Math.min(100, isGain ? (1 / ratio) * 100 : ratio * 100));

        return (
            <div className="space-y-3 sm:space-y-4">
                {/* ① Hero — StockSummaryHero와 같은 구조 */}
                <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <div className="text-xs text-content-muted">총 평가액</div>
                            <div className="mt-1 font-num text-2xl font-semibold text-content sm:text-3xl">
                                {krw(value)}
                            </div>
                            <div className="mt-0.5 font-num text-xs text-content-muted">
                                {Number(qty).toLocaleString(undefined, { maximumFractionDigits: 8 })} BTC
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span className="inline-flex items-center gap-1 rounded-full bg-brand-subtle px-2.5 py-1 text-xs font-medium text-content-secondary">
                                <span className="h-1.5 w-1.5 rounded-full bg-gain" aria-hidden="true" />
                                업비트 연동
                            </span>
                            {currentPrice != null && (
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-surface-border bg-surface-card-alt px-3 py-1.5 text-xs text-content-secondary">
                                    <span>BTC/KRW</span>
                                    <span className="font-num text-content">
                                        {Math.round(currentPrice).toLocaleString()}
                                    </span>
                                </span>
                            )}
                        </div>
                    </div>
                    {/* 모바일 3열은 셀 폭 82px 인데 18px 숫자가 119px 라 접힌다 (DAS-178).
                        가장 긴 값에 전폭을 주고 나머지를 2열로 — 순서는 그대로, sm: 이상은 3열 유지 */}
                    <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
                        <StatCell label="누적 투입원금" value={krw(principal)} wide />
                        <StatCell label="평가손익" value={krwSigned(pnl)} valueClass={pnlColor} />
                        <StatCell
                            label="수익률"
                            value={pct(pnlPct)}
                            valueClass={pnlPct == null ? "text-content-muted" : pnlColor}
                        />
                    </div>
                </div>

                {/* ② 적립 성과 차트 — 해외주식과 같은 컴포넌트, 문구만 다르다 */}
                <AccumulationChart
                    points={series?.points}
                    loading={series?.loading}
                    error={series?.error}
                    onRetry={series?.onRetry}
                    snapshotSince={series?.snapshotSince}
                    emptyMessage="아직 기록된 스냅샷이 없습니다. 다음 적재부터 곡선이 그려집니다."
                    dataStartSuffix="이전 구간은 업비트가 과거 평단을 주지 않아 소급 불가"
                />

                {/* ③ 평단 대비 현재가 */}
                {ratio != null && (
                    <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="text-sm font-semibold text-content-secondary">평단 대비 현재가</div>
                            <div className={`font-num text-sm font-semibold ${pnlColor}`}>{pct(pnlPct)}</div>
                        </div>
                        <div
                            className="mt-4 h-3 overflow-hidden rounded-full bg-surface-elevated"
                            role="img"
                            aria-label={`평단 ${krw(avgBuyPrice)}, 현재가 ${krw(currentPrice)}`}
                        >
                            <div
                                className={`h-full transition-[width] duration-700 ${isGain ? "bg-gain" : "bg-loss"}`}
                                style={{ width: `${fillPercent}%` }}
                            />
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2 sm:gap-3">
                            <div className="rounded-xl border border-surface-border bg-surface-card-alt px-3 py-2.5">
                                <div className="text-xs text-content-muted">평단</div>
                                <div className="mt-1 font-num text-base font-semibold text-content">
                                    {krw(avgBuyPrice)}
                                </div>
                            </div>
                            <div className="rounded-xl border border-surface-border bg-surface-card-alt px-3 py-2.5">
                                <div className="text-xs text-content-muted">현재가</div>
                                <div className={`mt-1 font-num text-base font-semibold ${pnlColor}`}>
                                    {krw(currentPrice)}
                                </div>
                            </div>
                        </div>
                        {onGoDelta && (
                            <button
                                type="button"
                                onClick={onGoDelta}
                                className="mt-4 min-h-[44px] w-full rounded-lg border border-surface-border text-xs font-medium text-content-secondary transition-colors hover:bg-surface-hover"
                            >
                                같은 계좌의 델타전략 보기
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export default BtcDcaPanel;
